import React, { useImperativeHandle, useRef } from "react";
import { Box, Divider } from "@mui/material";
import FeedbackModal from "./FeedbackModal";
import CodeSection from "./CodeSection";
import useCptVoting from "../hooks/useCptVoting";
import useCaseCodesExport from "../hooks/useCaseCodesExport";
import useToggleIndex from "../hooks/useToggleIndex";
import EmptyComponent from "../../../components/EmptyComponent";
import useGetAllCptsQuery from "../hooks/useGetAllCptsQuery";
import { getAuthValue } from "../../authModule/hooks/useAuthValue";
import useSelectedHospitalValue from "../../hospitalModule/hooks/useSelectedHospitalValue";
//@ts-ignore
import { useScreenshot } from "use-react-screenshot";
import { CPTType } from "../../codingModule/type/normalizeCodesForUi";
import { getConfidenceNumber } from "../helpers/caseCodesHelpers";

type CaseCodesProps = {
  caseId?: string;
  caseCodesRef?: any;
  apiType?: string;
};

const CaseCodes: React.FC<CaseCodesProps> = ({
  caseId,
  caseCodesRef,
  apiType,
}) => {
  const currentHospital = useSelectedHospitalValue();
  const auth = getAuthValue();
  const hospitalId = currentHospital?.uuid ?? auth.hospitalId;
  const containerRef = useRef(null);
  const [, takeScreenshot] = useScreenshot();

  const {
    index: expandedCptIndex,
    setIndex: setExpandedCptIndex,
    toggle: toggleCpt,
  } = useToggleIndex();
  const {
    index: expandedIcdIndex,
    setIndex: setExpandedIcdIndex,
    toggle: toggleIcdBase,
  } = useToggleIndex();
  const {
    index: expandedHcpcsIndex,
    setIndex: setExpandedHcpcsIndex,
    toggle: toggleHcpcs,
  } = useToggleIndex();

  const toggleIcd = (cptIndex: number) => {
    setExpandedCptIndex((prev) => (prev === cptIndex ? prev : null));
    toggleIcdBase(cptIndex);
  };

  const isBoth = apiType === "BOTH" || !apiType;

  const shouldLoadCpt = apiType === "CPT" || isBoth;
  const shouldLoadHcpcs = apiType === "HCPCS" || isBoth;

  const { data: cptData = [] } = useGetAllCptsQuery(
    hospitalId || "",
    caseId || "",
    "CPT",
    shouldLoadCpt
  );

  const { data: hcpcsData = [] } = useGetAllCptsQuery(
    hospitalId || "",
    caseId || "",
    "HCPCS",
    shouldLoadHcpcs
  );

  const hasAnyCodes =
    (cptData?.length ?? 0) > 0 || (hcpcsData?.length ?? 0) > 0;

  const hasVisibleCpt = cptData.some(
    (cpt: CPTType) => getConfidenceNumber(cpt.cpt_confidence) >= 25
  );

  const hasVisibleHcpcs = hcpcsData.some(
    (cpt: CPTType) => getConfidenceNumber(cpt.cpt_confidence) >= 25
  );

  const {
    feedbackModalOpen,
    feedbackText,
    setFeedbackText,
    handleThumbUp,
    handleThumbDown,
    handleCloseModal,
    handleSubmitFeedback,
  } = useCptVoting(caseId, hospitalId);

  const { forceExpandAll, handleExportScreenshot, handleExportCsv } =
    useCaseCodesExport({
      containerRef,
      takeScreenshot,
      hasAnyCodes,
      cptData,
      hcpcsData,
      apiType,
      isBoth,
      clearExpandedIndices: () => {
        setExpandedCptIndex(null);
        setExpandedIcdIndex(null);
        setExpandedHcpcsIndex(null);
      },
    });

  useImperativeHandle(caseCodesRef, () => ({
    exportCaseCodesScreenshot: handleExportScreenshot,
    exportCaseCodesCsv: handleExportCsv,
  }));

  if (!hasAnyCodes) {
    return (
      <Box
        height="60vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <EmptyComponent title="No data to display" />
      </Box>
    );
  }

  return (
    <Box ref={containerRef} p={1}>
      {shouldLoadCpt && hasVisibleCpt && (
        <CodeSection
          list={cptData}
          sectionType="CPT"
          expandedIndex={expandedCptIndex}
          onToggle={toggleCpt}
          expandedIcdIndex={expandedIcdIndex}
          onToggleIcd={toggleIcd}
          forceExpandAll={forceExpandAll}
          onThumbUp={handleThumbUp}
          onThumbDown={handleThumbDown}
        />
      )}

      {isBoth && hasVisibleCpt && hasVisibleHcpcs && <Divider sx={{ my: 3 }} />}

      {shouldLoadHcpcs && hasVisibleHcpcs && (
        <CodeSection
          list={hcpcsData}
          sectionType="HCPCS"
          expandedIndex={expandedHcpcsIndex}
          onToggle={toggleHcpcs}
          expandedIcdIndex={expandedIcdIndex}
          onToggleIcd={toggleIcd}
          forceExpandAll={forceExpandAll}
          onThumbUp={handleThumbUp}
          onThumbDown={handleThumbDown}
        />
      )}
      <FeedbackModal
        open={feedbackModalOpen}
        onClose={handleCloseModal}
        feedbackText={feedbackText}
        onFeedbackChange={setFeedbackText}
        onSubmit={handleSubmitFeedback}
      />
    </Box>
  );
};

export default CaseCodes;
